import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Product } from '@spartacus/core';
import { CurrentProductService } from '@spartacus/storefront';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { RouterLink } from '@angular/router';

import { IDataTableColumn, SdTableComponent } from '@seb/web-design-system/tables';
import {
  BrandsEnum,
  BrandThemesEnum,
  DataTableColumnTypeEnum,
  DeviceService, IComponentWrapper,
  IconSizeEnum,
  SdBannerComponent,
  SdComponentWrapperComponent,
  SdFormInputComponent,
  SdIconComponent,
  SdModalDefaultComponent,
  SizesEnum,
  ThemesEnum
} from '@seb/web-design-system/core';
import { LameConfig, UrlMapping } from '@seb/web-common/core/models';
import { BrandCornerMapper, HeadingParagraphMapper } from '@seb/web-common/core/mappers';
import { BaseStoreService, UrlMappingService } from '@seb/web-common/core/services';
import '@seb/web-common/core/models/augmented-spartacus-core.model';

export interface EntryProductMadeFor {
  name?: { label: string };
  reference?: { label: string };
  category?: { label: string };
}

export interface TableProductMadeFor {
  entries: EntryProductMadeFor[];
  columns: IDataTableColumn[];
}

@Component({
  selector: 'wc-designed-for',
  templateUrl: './designed-for.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdComponentWrapperComponent,
    SdFormInputComponent,
    SdIconComponent,
    SdTableComponent,
    RouterLink,
    SdModalDefaultComponent,
    SdBannerComponent,
    TranslatePipe
  ]
})
export class DesignedForComponent implements OnInit, OnDestroy {
  protected baseStoreService = inject(BaseStoreService);

  table: TableProductMadeFor;
  numberOfLines: number | undefined;
  themesEnum = ThemesEnum;
  isRefModalOpened = false;
  urlImage = '/assets/images/asset-accessory-reference.png';
  entries: EntryProductMadeFor[];
  isNoProduct = false;
  numberOfLinesToKeep: number;
  contactUsUrl = '';
  wrapperContent: IComponentWrapper;
  lameConfig: LameConfig;
  iconSize = IconSizeEnum;

  @Input() brand = BrandsEnum.default;

  @Output() hasLoaded = new EventEmitter<void>();

  @Input() pageTemplate = '';

  private readonly destroy$: Subject<void> = new Subject();

  constructor(
    private readonly currentProductService: CurrentProductService,
    private readonly translateService: TranslateService,
    private readonly deviceService: DeviceService,
    private readonly urlMappingService: UrlMappingService,
    private cdRef: ChangeDetectorRef,
    private headingParagraphMapper: HeadingParagraphMapper,
    private brandCornerMapper: BrandCornerMapper
  ) {}

  ngOnInit(): void {
    const baseStore = this.baseStoreService.baseStore;
    const isHob = !!baseStore.houseOfBrandConfig?.brand?.length;
    this.urlImage = isHob ? '/assets/images/hob-asset-accessory-reference.png' : this.urlImage;

    this.lameConfig = this.brandCornerMapper.toWcConfig(this.brand)?.pdp?.designedFor;
    const contactUs$: Observable<UrlMapping> = this.urlMappingService.getUrlMappingByCode(
      '/consumer-services/contact-us'
    );
    const isMobile$: Observable<boolean> = this.deviceService.isMobile;
    const currentProduct$: Observable<Product> = this.currentProductService.getProduct().pipe(filter((p) => !!p));
    combineLatest([isMobile$, currentProduct$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([isMobile, currentProduct]) => {
        this.numberOfLinesToKeep = isMobile ? 5 : 10;
        this.numberOfLines = this.numberOfLinesToKeep;
        this.table = this.buildTable(currentProduct.productsDesignedFor, isMobile);
        this.entries = this.table.entries;
        const richParagraphData = {
          heading: {
            headline: this.translateService.instant('accessory.designedFor.title', {
              numberOfProducts: currentProduct.productsDesignedFor.length.toString()
            }),
            description: this.translateService.instant('accessory.designedFor.description')
          },
          size: SizesEnum.l,
          isUnderlined: !!this.lameConfig?.titleUnderlined,
          titleThemesConfig: this.lameConfig?.titleTheme || BrandThemesEnum.brand
        };
        this.wrapperContent = {
          links: [],
          footNotes: [],
          heading: this.headingParagraphMapper.toHeadingParagraph(richParagraphData)
        };
        this.hasLoaded.emit();
        this.cdRef.markForCheck();
      });
    contactUs$?.pipe(takeUntil(this.destroy$)).subscribe((url) => (this.contactUsUrl = url?.value || '/'));
  }

  onEmit(event: string): void {
    this.entries = this.table.entries.filter((entry) => entry?.reference?.label?.includes(event.toUpperCase()));
    this.isNoProduct = this.entries.length === 0;
    this.numberOfLines = this.numberOfLinesToKeep;
  }

  updateNumberOfLines(): void {
    this.numberOfLines += this.numberOfLinesToKeep;
  }

  openModalRef(): void {
    this.isRefModalOpened = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildTable(products: Product[], isMobile: boolean): TableProductMadeFor {
    const productDetails: EntryProductMadeFor[] = [];
    products.forEach((product: Product) =>
      productDetails.push({
        name: { label: product.name },
        reference: { label: product.commercialCode },
        category: product.sisCategory ? { label: product.sisCategory } : undefined
      })
    );
    const columns: IDataTableColumn[] = [
      {
        id: 'name',
        label: {
          label: this.translateService.instant('accessory.designedFor.product')
        },
        width: undefined,
        type: DataTableColumnTypeEnum.media
      },
      {
        id: 'reference',
        label: {
          label: this.translateService.instant('accessory.designedFor.reference')
        },
        width: undefined,
        type: DataTableColumnTypeEnum.media
      },
      {
        id: 'category',
        label: {
          label: this.translateService.instant('accessory.designedFor.category')
        },
        width: undefined,
        type: DataTableColumnTypeEnum.media
      }
    ];
    return {
      entries: productDetails,
      columns: isMobile ? columns.slice(0, 2) : columns
    };
  }
}
